import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, useCallback } from "react";

import { getEventsByOrderId, getOrders } from "./api";
import { ordersStore } from "./store";
import { OrderGetDto } from "./dto";

function useFetchOrders(params) {
  const { setOrders } = ordersStore();
  const [orders, _setOrders] = useState([]);

  const { data, isError, error, refetch, status, isFetching } = useQuery({
    queryKey: ["orders"],
    queryFn: () => getOrders(params),
    enabled: false,
  });

  useEffect(() => {
    if (data) {
      const mappedOrders = data.map((order) => new OrderGetDto(order));
      setOrders(mappedOrders);
      _setOrders(mappedOrders);
    }
  }, [data, setOrders]);

  return { data, isError, error, orders, refetch, status, isFetching };
}

function useFetchEventsByOrderId(query) {
  const { setEvents } = ordersStore();
  const [events, _setEvents] = useState([]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["events"],
    queryFn: () => getEventsByOrderId(query),
    enabled: false,
  });

  useEffect(() => {
    if (data) {
      setEvents(data);
      _setEvents(data);
    }
  }, [data, setEvents]);

  return { data, isLoading, isError, error, events, refetch };
}

function useFetchOrdersById(orderId) {
  const stateOrder = ordersStore(
    useCallback(
      (state) => state.orders.find((s) => s.id === orderId),
      [orderId]
    )
  );

  const localStorageOrder =
    !!localStorage.getItem("CURRENT_ORDER") &&
    JSON.parse(localStorage.getItem("CURRENT_ORDER"));

  return stateOrder ? stateOrder : localStorageOrder;
}

function useFetchOrdersEditedById(orderId) {
  const localStorageOrders =
    !!localStorage.getItem("EDITED_ORDERS") &&
    JSON.parse(localStorage.getItem("EDITED_ORDERS"));

  return localStorageOrders && localStorageOrders.length > 0
    ? localStorageOrders.includes(orderId.toString())
    : false;
}

function useStoreEditedOrders(orderId) {
  const localStorageOrders =
    !!localStorage.getItem("EDITED_ORDERS") &&
    JSON.parse(localStorage.getItem("EDITED_ORDERS"));

  const editedOrders =
    localStorageOrders && localStorageOrders.length > 0
      ? [...localStorageOrders, orderId]
      : [orderId];

  return localStorage.setItem("EDITED_ORDERS", JSON.stringify([...editedOrders]));
}

function useFilteredOrders() {
  return ordersStore.getState().filteredOrders;
}

function useFilters() {
  return {
    selectedFilters: ordersStore.getState().selectedFilters,
    addFilter: (filter) => ordersStore.getState().addSelectedFilter(filter),
    removeFilter: (filter) =>
      ordersStore.getState().removeSelectedFilter(filter),
    resetFilters: () => ordersStore.getState().resetFilters(),
  };
}

function useFetchOrdersBy(filters) {
  return ordersStore.getState().filterOrders(filters);
}

export {
  useFetchOrders,
  useFetchOrdersById,
  useFilteredOrders,
  useFetchOrdersBy,
  useFetchEventsByOrderId,
  useFilters,
  useFetchOrdersEditedById,
  useStoreEditedOrders
};
